import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, input, output } from '@angular/core';
import { SharedMaterialUIModule } from '../modules/shared-material-ui.module';
import { SHARED_UTILS } from '../modules/shared-utils.module';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

export type DataTablePageChangedEvent = {
  pageNumber: number;
  pageSize: number;
};

export type DataTableColumn<T> = {
  id: string;
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
  headerClass?: string;
  cellClass?: string;
};

type DataTableActionContext<T> = {
  $implicit: T;
};

@Component({
  selector: 'data-table',
  imports: [NgClass, NgTemplateOutlet, ...SharedMaterialUIModule, MatTableModule, MatProgressBarModule, MatPaginatorModule],
  templateUrl: './data-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DataTableComponent<T> {
  readonly utils = SHARED_UTILS();
  readonly title = input.required<string>();
  readonly subtitle = input<string | null>(null);

  readonly rows = input<T[]>([]);
  readonly columns = input<DataTableColumn<T>[]>([]);
  readonly actionsTemplate = input<TemplateRef<DataTableActionContext<T>> | null>(null);

  protected readonly displayedColumns = computed(() => {
    const columnIds = this.columns().map((column) => column.id);
    return this.actionsTemplate() ? [...columnIds, 'actions'] : columnIds;
  });

  readonly loading = input(false);
  readonly loadingMessage = input('Loading...');
  readonly emptyMessage = input('No records found.');

  readonly showRefresh = input(false);
  readonly searchEnabled = input(false);
  readonly searchLabel = input('Search');
  readonly searchPlaceholder = input('Search');
  readonly searchTerm = input('');

  readonly paginationEnabled = input(false);
  readonly pageNumber = input(1);
  readonly pageSize = input(25);
  readonly totalCount = input(0);
  readonly totalPages = input(0);
  readonly showPageSizeOptions = input(true);
  readonly pageSizeOptions = input<number[]>([5, 10, 25, 50, 100]);
  readonly customCardClass = input<string>('');

  readonly showActionButton = input<boolean>(false);
  readonly actionIcon = input<string | null>('add');
  readonly tableActionData = input<any>(null);
  readonly tableActionClick = output<string>();
  doTableAction(action: string, route?: string) {
    switch (action) {
      case 'pageChange':
        this.utils.navigateTo(route+'/'+this.tableActionData().id);
        break;
      default:
        this.tableActionClick.emit(action);
        break;
    }
  }

  readonly refreshed = output<void>();
  readonly searchChanged = output<string>();
  readonly pageChanged = output<DataTablePageChangedEvent>();

  protected applySearch(searchValue: string): void {
    this.searchChanged.emit(searchValue.trim());
  }

  // MatPaginator uses a 0-based pageIndex; the API uses 1-based page numbers.
  protected onPage(event: PageEvent): void {
    this.pageChanged.emit({
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    });
  }

  protected resolveCellValue(column: DataTableColumn<T>, row: T): string {
    const value = column.value(row);
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return String(value);
  }

  protected trackByColumnId(_index: number, column: DataTableColumn<T>): string {
    return column.id;
  }
}